import { FormInstance } from 'antd';
import { formatAmount } from './formatAmount';

type TaxHistory = {
  taxIncreaseRate: number | null;
  taxPaid: number | null;
  time: number | null;
  value: number | null;
  valueIncreaseRate: number | null;
};

const getLatestAnnualTaxAmount = (taxHistory: TaxHistory[]): number => {
  if (!taxHistory || taxHistory.length === 0) return 0;

  // Filter and sort valid tax history entries
  const validEntries = taxHistory
    .filter(entry => entry.taxPaid !== null && entry.time !== null)
    .sort((a, b) => b.time! - a.time!); // Descending by time

  if (validEntries.length === 0) return 0;

  return parseFloat(formatAmount(validEntries[0].taxPaid!));
};

type FieldValues = {
  purchasePrice?: number;
  propertyValue?: number;
  purchasePriceActive?: boolean;
  propertyTaxesPercentage?: number;
  suplimentalData?: string;
  zipCode?: string;
}

export const calculatePropertyTaxes = (
  form: FormInstance,
  loanIndex: string | number,
  {
    purchasePrice,
    propertyValue,
    purchasePriceActive,
    propertyTaxesPercentage,
    suplimentalData,
    zipCode,
  }: FieldValues = {}
): number => {
  const propertyTaxesPercentageValue = propertyTaxesPercentage ??
    (parseFloat(form.getFieldValue(['loans', loanIndex, 'propertyTaxesPercentage', 'value'])) || 0);

  const suplimentalDataValue = suplimentalData ??
    (form.getFieldValue(['loans', loanIndex, 'suplimentalData']) || '{}');

  const zipCodeValue = zipCode ?? form.getFieldValue(['loans', loanIndex, 'zipCode']);

  const purchasePriceValue =
    purchasePrice ?? (parseFloat(form.getFieldValue(['loans', loanIndex, 'purchasePrice', 'value'])) || 0);

  const propertyValueValue =
    propertyValue ?? (parseFloat(form.getFieldValue(['loans', loanIndex, 'externalLoan', 'propertyValue'])) || 0);

  const purchasePriceActiveValue = purchasePriceActive ?? form.getFieldValue(['tableView', 'purchasePrice', 'active']);

  const price = purchasePriceActiveValue ? purchasePriceValue : propertyValueValue;

  const parsedData = JSON.parse(suplimentalDataValue);

  const state = parsedData?.state;
  const county = parsedData?.county;
  const taxHistory = parsedData?.taxHistory;

  // Convert percentage rate to decimal
  const rateDecimal = propertyTaxesPercentageValue / 100;
  let annualTax = 0;

  switch (state) {
    // States with fractional assessment
    case 'AL': // Alabama
    case 'AZ': // Arizona
    case 'MS': // Mississippi
      annualTax = rateDecimal * (0.1 * price);
      break;
    case 'AR': // Arkansas
      annualTax = rateDecimal * (0.2 * price) - 375;
      break;
    case 'CT': // Connecticut
      annualTax = rateDecimal * (0.7 * price);
      break;
    case 'GA': // Georgia
      annualTax = rateDecimal * (0.4 * price);
      break;
    case 'IL': // Illinois
      // eslint-disable-next-line no-case-declarations
      const assessmentRatio = county === 'Cook County' ? 0.1 : 0.333;

      annualTax = rateDecimal * (assessmentRatio * price);
      break;
    case 'IN': // Indiana
      // eslint-disable-next-line no-case-declarations
      const uncappedTax = rateDecimal * (0.35 * price);

      annualTax = Math.min(uncappedTax, 0.01 * price);
      break;
    case 'IA': // Iowa
      annualTax = rateDecimal * (0.54 * price);
      break;
    case 'KS': // Kansas
      annualTax = rateDecimal * (0.115 * price);
      break;
    case 'LA': // Louisiana
      // eslint-disable-next-line no-case-declarations
      const taxableValue = Math.max(0.1 * price - 7500, 0);

      annualTax = rateDecimal * taxableValue;
      break;
    case 'MT': // Montana
      annualTax = rateDecimal * (0.0135 * price);
      break;
    case 'ND': // North Dakota
      annualTax = rateDecimal * (0.045 * price);
      break;
    case 'OH': // Ohio
      annualTax = rateDecimal * (0.35 * price);
      break;
    case 'OK': // Oklahoma
      annualTax = rateDecimal * (0.11 * price);
      break;
    case 'SC': // South Carolina
      annualTax = rateDecimal * (0.04 * price);
      break;
    case 'TN': // Tennessee
      annualTax = rateDecimal * (0.25 * price);
      break;
    case 'UT': // Utah
      annualTax = rateDecimal * (0.55 * price);
      break;
    case 'WV': // West Virginia
      annualTax = rateDecimal * (0.6 * price);
      break;

    // States with exemptions
    case 'FL': // Florida
      annualTax = rateDecimal * Math.max(price - 50000, 0);
      break;
    case 'ID': // Idaho
      annualTax = rateDecimal * Math.max(price - 125000, 0);
      break;
    case 'MI': // Michigan
      annualTax = rateDecimal * (0.5 * price);
      break;
    case 'TX': // Texas
      annualTax = rateDecimal * Math.max(price - 40000, 0);
      break;

    // States with tax caps/no reassessment
    case 'OR': // Oregon
      annualTax = getLatestAnnualTaxAmount(taxHistory) * 1.03;
      break;
    case 'PA': // Pennsylvania
      annualTax = getLatestAnnualTaxAmount(taxHistory);
      break;

    // New York (special handling)
    case 'NY': // New York
      // eslint-disable-next-line no-case-declarations
      const nycZipPrefixes = ['100', '112', '111', '113', '114', '104', '103'];

      // eslint-disable-next-line no-case-declarations
      const isNYCClass1 = nycZipPrefixes.some((prefix) => zipCodeValue.startsWith(prefix));

      if (isNYCClass1) {
        annualTax = getLatestAnnualTaxAmount(taxHistory) * 1.06;
      } else {
        // Non-NYC areas
        annualTax = rateDecimal * price;
      }
      break;

    // Default full-value assessment states:
    // AK, CA, DE, HI, KY, ME, MD, MN, NE, NV, NH, NJ, NM, NC, RI, SD, VT, VA, WA, WI, WY, CO, MO
    default:
      annualTax = rateDecimal * price;
  }

  // Convert annual tax to monthly and format
  const monthlyTax = annualTax / 12;

  return parseFloat(formatAmount(monthlyTax));
};
